import { makeAutoObservable, observable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { signedEditionService } from "../services/signedEditionService";
import { signedEditionDisplayId } from "./SignedEditionCommonStore";
import { SignedEdition } from "../../../api/graphql";
import { diaDeCalendario } from "../../../common/calendarDate";
import { getAssociatedDocumentSignedEditionValues } from "../../associatedDocumentSignedEdition/store/AssociatedDocumentSignedEditionCommonStore";
import { ColumnWidthInfo } from "../../common/services/crudServices";
import { getSignedEditionBlockchainCertValues } from "../../signedEditionBlockchainCert/store/SignedEditionBlockchainCertCommonStore";
import {
  downloadWithAuth,
  fetchFileBlob,
} from "../../../common/utils/downloadWithAuth";
import { CrudRows } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import { InitializeParams } from "../../../store/types";

export class SignedEditionViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = signedEditionService;
  signedEditionInstance: SignedEdition | null = null;
  cardState: Record<string, boolean> = {
    signedEdition: true,
    dndaTracking: false,
    sadaicTracking: false,
    systemDates: false,
  };
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  dndaStateValue: string = "";
  sadaicStateValue: string = "";
  publicationDateValue: string | null = null;
  dndaExpedienteNumberValue: string = "";
  dndaDocumentsColumnWidths: Record<string, number | string> = {};
  dndaDocumentsRows: CrudRows = {};
  ejemplarPdfFiles: CrudRows = {};
  dndaComprobantePdfFiles: CrudRows = {};
  blockchainCertificatesColumnWidths: Record<string, number | string> = {};
  blockchainCertificatesRows: CrudRows = {};
  dndaUploadLinkValue: string = "";
  dndaUploadKeyReceivedAtValue: Date | null = null;
  dndaUploadKeyExpiresAtValue: Date | null = null;
  dndaComprobanteReceivedAtValue: Date | null = null;
  dndaLastComprobantePollAtValue: Date | null = null;
  dndaLastOperationalErrorValue: string = "";
  sadaicDeliveredAtValue: Date | null = null;
  sadaicLastOperationalErrorValue: string = "";
  createdAt_Value: Date | null = null;
  lastUpdatedAt_Value: Date | null = null;
  data_Value: any = null;
  originalId_Value: string = "";
  pdfViewerOpen: boolean = false;
  pdfViewerUrl: string = "";
  pdfViewerTitle: string = "";

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;
    this.dndaDocumentsColumnWidths = { file: 200 };
    this.blockchainCertificatesColumnWidths = { file: 200 };

    makeAutoObservable(this, { data_Value: observable.ref });
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadSignedEditionById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.signedEditionOne({ id });
    this.signedEditionInstance = result.signedEditionOne;
    if (this.signedEditionInstance) {
      this.loadValues(this.signedEditionInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.signedEditionInstance) {
      return signedEditionDisplayId(this.signedEditionInstance, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.SignedEditionSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  returnedData(_params: CallbackDataParams): any {}

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  get dndaDocumentsVisibleColumns() {
    return [
      {
        name: "file",
        title: this.langStore.AssociatedDocumentSignedEditionFileLabel,
        width: this.dndaDocumentsColumnWidths["file"],
      },
    ];
  }

  dndaDocumentsSetColumnWiths(x: ColumnWidthInfo[]) {
    const values: Record<string, number | string> = {};
    for (const item of x) {
      values[item.columnName] = item.width;
    }
    this.dndaDocumentsColumnWidths = values;
  }

  get dndaDocumentsRowsDyn() {
    return this.dndaDocumentsRows;
  }

  get blockchainCertificatesVisibleColumns() {
    return [
      {
        name: "file",
        title: this.langStore.SignedEditionBlockchainCertFileLabel,
        width: this.blockchainCertificatesColumnWidths["file"],
      },
    ];
  }

  blockchainCertificatesSetColumnWiths(x: ColumnWidthInfo[]) {
    const values: Record<string, number | string> = {};
    for (const item of x) {
      values[item.columnName] = item.width;
    }
    this.blockchainCertificatesColumnWidths = values;
  }

  get blockchainCertificatesRowsDyn() {
    return this.blockchainCertificatesRows;
  }

  *fileDownload(props: any, fieldName: string): any {
    yield downloadWithAuth({
      entityName: "SignedEdition",
      fieldName: fieldName,
      instanceId: this.signedEditionInstance?.id!,
      fileName: props.name,
    });
  }

  openPdfViewer(url: string, title: string) {
    this.pdfViewerUrl = url;
    this.pdfViewerTitle = title;
    this.pdfViewerOpen = true;
  }

  closePdfViewer() {
    if (this.pdfViewerUrl) {
      window.URL.revokeObjectURL(this.pdfViewerUrl);
    }
    this.pdfViewerOpen = false;
    this.pdfViewerUrl = "";
    this.pdfViewerTitle = "";
  }

  *viewFilePdf(props: any, fieldName: string): any {
    try {
      const blob = yield fetchFileBlob({
        entityName: "SignedEdition",
        fieldName: fieldName,
        instanceId: this.signedEditionInstance?.id!,
        fileName: props.name,
      });
      if (!blob) return;
      this.openPdfViewer(window.URL.createObjectURL(blob), props.name);
    } catch (error) {
      console.error("Error loading PDF:", error);
    }
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.signedEditionInstance = null;
    this.dndaStateValue = "";
    this.sadaicStateValue = "";
    this.publicationDateValue = null;
    this.dndaExpedienteNumberValue = "";
    this.dndaDocumentsRows = {};
    this.ejemplarPdfFiles = {};
    this.dndaComprobantePdfFiles = {};
    this.blockchainCertificatesRows = {};
    this.dndaUploadLinkValue = "";
    this.dndaUploadKeyReceivedAtValue = null;
    this.dndaUploadKeyExpiresAtValue = null;
    this.dndaComprobanteReceivedAtValue = null;
    this.dndaLastComprobantePollAtValue = null;
    this.dndaLastOperationalErrorValue = "";
    this.sadaicDeliveredAtValue = null;
    this.sadaicLastOperationalErrorValue = "";
    this.createdAt_Value = null;
    this.lastUpdatedAt_Value = null;
    this.data_Value = null;
    this.originalId_Value = "";
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelSignedEdition() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return false;
  }

  editSignedEdition() {
    const id = this.signedEditionInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/signedEdition/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printSignedEdition(handlePrint: () => void): void {
    handlePrint();
  }

  *closeSignedEdition(): any {
    yield this.close();
  }

  loadValues(instance: SignedEdition) {
    this.idValue = instance.id;
    this.dndaStateValue = instance.dndaState?.id || "";
    this.sadaicStateValue = instance.sadaicState?.id || "";
    this.publicationDateValue = diaDeCalendario(instance.publicationDate);
    this.dndaExpedienteNumberValue = instance.dndaExpedienteNumber || "";
    this.dndaDocumentsRows = instance.dndaDocuments
      ? Object.assign(
          {},
          ...instance.dndaDocuments.map((x) => {
            const processedRow = getAssociatedDocumentSignedEditionValues(
              x,
              this.langStore,
              false
            );
            return {
              [`__${x.id}`]: { ...processedRow, __status__: "original" },
            };
          })
        )
      : {};

    this.ejemplarPdfFiles = instance.ejemplarPdf_data
      ? {
          [instance.ejemplarPdf_data.name]: {
            name: instance.ejemplarPdf_data.name,
            formattedSize: formatBytes(instance.ejemplarPdf_data?.size || 0),
            mimetype: instance.ejemplarPdf_data?.mimetype,
            id: instance.ejemplarPdf_data.name,
            __status__: "original",
          },
        }
      : {};
    this.dndaComprobantePdfFiles = instance.dndaComprobantePdf_data
      ? {
          [instance.dndaComprobantePdf_data.name]: {
            name: instance.dndaComprobantePdf_data.name,
            formattedSize: formatBytes(
              instance.dndaComprobantePdf_data?.size || 0
            ),
            mimetype: instance.dndaComprobantePdf_data?.mimetype,
            id: instance.dndaComprobantePdf_data.name,
            __status__: "original",
          },
        }
      : {};
    this.blockchainCertificatesRows = instance.blockchainCertificates
      ? Object.assign(
          {},
          ...instance.blockchainCertificates.map((x) => {
            const processedRow = getSignedEditionBlockchainCertValues(
              x,
              this.langStore,
              false
            );
            return {
              [`__${x.id}`]: { ...processedRow, __status__: "original" },
            };
          })
        )
      : {};

    this.dndaUploadLinkValue = instance.dndaUploadLink || "";
    this.dndaUploadKeyReceivedAtValue = instance.dndaUploadKeyReceivedAt
      ? new Date(instance.dndaUploadKeyReceivedAt)
      : null;
    this.dndaUploadKeyExpiresAtValue = instance.dndaUploadKeyExpiresAt
      ? new Date(instance.dndaUploadKeyExpiresAt)
      : null;
    this.dndaComprobanteReceivedAtValue = instance.dndaComprobanteReceivedAt
      ? new Date(instance.dndaComprobanteReceivedAt)
      : null;
    this.dndaLastComprobantePollAtValue = instance.dndaLastComprobantePollAt
      ? new Date(instance.dndaLastComprobantePollAt)
      : null;
    this.dndaLastOperationalErrorValue =
      instance.dndaLastOperationalError || "";
    this.sadaicDeliveredAtValue = instance.sadaicDeliveredAt
      ? new Date(instance.sadaicDeliveredAt)
      : null;
    this.sadaicLastOperationalErrorValue =
      instance.sadaicLastOperationalError || "";
    this.createdAt_Value = new Date(instance.createdAt_);
    this.lastUpdatedAt_Value = new Date(instance.lastUpdatedAt_);
    this.data_Value = instance.data_;
    this.originalId_Value = instance.originalId_;
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadSignedEditionById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
